/**
 * Exact time reversal of an ease (D§6.4 family of segment operations; contract `EaseApi.reverse`):
 *
 *   f(x) -> 1 - f(1 - x)
 *   tween: In <-> Out of the same family (InOut and Linear are their own reversal), slice [l, r] -> [1 - r, 1 - l]
 *   bezier (x1, y1, x2, y2) -> (1 - x2, 1 - y2, 1 - x1, 1 - y1)
 *   hold: unchanged (the jump lives in the keys' vIn / vOut, which the caller swaps)
 *
 * Mathematically exact for every family: Phira's Out is defined as 1 - In(1 - x) and its InOut is symmetric, so
 * 1 - c(1 - x) of the clamped curve c of (f, [l, r]) is the clamped curve of (reverse f, [1 - r, 1 - l]).
 *
 * Complements are computed so that reverse(reverse(e)) returns e exactly for the values the editor meets (an exact
 * involution on all doubles is impossible: 1 - v rounds, so several doubles share a complement). In order:
 *  1. f32-anchored binary pairs: when 1 - v is exact in f64 (1 - (1 - v) === v) and v or 1 - v is an f32 value,
 *     use 1 - v. Covers every f32 value >= 2^-29 (what Phira reads) and every dyadic value (0.25 <-> 0.75).
 *  2. short decimals: when v's shortest form and its decimal complement both have at most 15 significant digits,
 *     complement in decimal (0.1 <-> 0.9, 0.3 <-> 0.7, 1.3 <-> -0.3; plain f64 would turn 0.9 into
 *     0.09999999999999998). Covers every value typed in the editor and every short JSON decimal.
 *  3. otherwise 1 - v in f64.
 * Each rule maps its result back with the same rule, so the round trip is exact inside each class; outside them it
 * may move a value by one f64 ulp, far below the f32 precision Phira reads it with.
 */

import { catalogEntry, catalogVariant } from './catalog';
import type { BezierPoints, Ease, EaseVariant } from './types';

const MAX_SHORT_DIGITS = 15;

interface Decimal {
  /** Signed integer significand. */
  readonly sig: bigint;
  /** Power of ten. */
  readonly exp: number;
}

function parseDecimal(v: number): Decimal | null {
  const m = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]\d+))?$/.exec(String(v));
  if (m === null) return null;
  const frac = m[3] ?? '';
  const digits = `${m[2] ?? '0'}${frac}`;
  const exp = (m[4] === undefined ? 0 : Number.parseInt(m[4], 10)) - frac.length;
  const sig = BigInt(digits);
  return { sig: m[1] === '-' ? -sig : sig, exp };
}

function significantDigits(n: bigint): number {
  let s = (n < 0n ? -n : n).toString();
  s = s.replace(/0+$/, '');
  return s === '' ? 0 : s.length;
}

function isF32(v: number): boolean {
  return Math.fround(v) === v;
}

/** Decimal complement 1 - v when v and the result are short decimals (<= 15 significant digits), else null. */
function decimalComplement(v: number): number | null {
  const d = parseDecimal(v);
  if (d === null || significantDigits(d.sig) > MAX_SHORT_DIGITS) return null;
  const scale = Math.max(0, -d.exp);
  const one = 10n ** BigInt(scale);
  const value = d.sig * 10n ** BigInt(d.exp + scale);
  const diff = one - value;
  if (significantDigits(diff) > MAX_SHORT_DIGITS) return null;
  return Number(`${diff.toString()}e-${String(scale)}`);
}

/** 1 - v, chosen so that complementUnit(complementUnit(v)) === v (see the module comment for the rules). */
export function complementUnit(v: number): number {
  const binary = 1 - v;
  if (!Number.isFinite(v)) return binary;
  if (1 - binary === v && (isF32(v) || isF32(binary))) return binary;
  return decimalComplement(v) ?? binary;
}

const OPPOSITE: Readonly<Record<EaseVariant, EaseVariant>> = { in: 'out', out: 'in', inout: 'inout' };

/** RPE easingType of the time-reversed curve (In <-> Out); ids without a curve (linear, out of range) stay. */
export function reversedEasingType(easingType: number): number {
  const entry = catalogEntry(easingType);
  if (entry === null || entry.variant === null) return easingType;
  return catalogVariant(entry.family, OPPOSITE[entry.variant])?.easingType ?? easingType;
}

export function reverse(e: Ease): Ease {
  switch (e.kind) {
    case 'hold':
      return e;
    case 'tween':
      return {
        kind: 'tween',
        id: reversedEasingType(e.id),
        left: complementUnit(e.right),
        right: complementUnit(e.left),
      };
    case 'bezier': {
      const [x1, y1, x2, y2] = e.p;
      const p: BezierPoints = [complementUnit(x2), complementUnit(y2), complementUnit(x1), complementUnit(y1)];
      return { kind: 'bezier', p };
    }
  }
}
